/**
 * CooperDocumentsPanel.jsx
 * 
 * Permet au COOPER'S connecté de :
 *  - Téléverser ses documents personnels dans la GED (catégorie + libellé libre)
 *  - Lister, télécharger et supprimer ses documents
 *
 * Catégories : récupérées depuis /api/coopers/me/documents/categories
 * (prédéfinies + personnalisées Super Admin via admin_ged_document_types).
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Upload,
  FileText,
  Download,
  Trash2,
  Loader2,
  FolderOpen,
  ShieldCheck,
  Tag,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function formatSize(bytes) {
  if (!bytes) return '0 Ko';
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} Ko`;
  return `${(kb / 1024).toFixed(1)} Mo`;
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

export default function CooperDocumentsPanel() {
  const { getAuthHeader } = useAuth();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('cooper_autre');
  const [customLabel, setCustomLabel] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);

  const loadCategories = async () => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/coopers/me/documents/categories`,
        { headers: getAuthHeader() },
      );
      setCategories(res.data.categories || []);
    } catch (err) {
      console.error('Erreur chargement catégories', err);
    }
  };

  const loadDocuments = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/coopers/me/documents`,
        { headers: getAuthHeader() },
      );
      setDocuments(res.data.documents || []);
    } catch {
      toast.error('Erreur lors du chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
    loadDocuments();
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 20 Mo)');
      return;
    }
    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('category_value', selectedCategory);
    if (customLabel.trim()) {
      formData.append('custom_label', customLabel.trim());
    }
    setUploading(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/coopers/me/documents`,
        formData,
        {
          headers: {
            ...getAuthHeader(),
            'Content-Type': 'multipart/form-data',
          },
        },
      );
      toast.success('Document téléversé avec succès');
      setSelectedFile(null);
      setCustomLabel('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      await loadDocuments();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Erreur lors de l’upload';
      toast.error(typeof msg === 'string' ? msg : 'Erreur lors de l’upload');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/coopers/me/documents/${doc.ged_id}/download`,
        { headers: getAuthHeader(), responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.original_filename || doc.filename || 'document';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const handleDelete = async (doc) => {
    if (
      !window.confirm(
        `Supprimer ce document "${doc.description || doc.doc_type_label}" ?`,
      )
    ) {
      return;
    }
    try {
      await axios.delete(
        `${BACKEND_URL}/api/coopers/me/documents/${doc.ged_id}`,
        { headers: getAuthHeader() },
      );
      toast.success('Document supprimé');
      await loadDocuments();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  return (
    <div className="space-y-6" data-testid="cooper-documents-panel">
      {/* Bandeau visibilité */}
      <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
        <ShieldCheck className="h-5 w-5 text-emerald-700 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-emerald-900">
          <p className="font-semibold mb-0.5">Espace privé sécurisé</p>
          <p className="text-emerald-800/80">
            Vos documents sont rattachés à votre profil COOPER&apos;S et visibles
            uniquement par vous et le Super Administrateur. Formats acceptés : PDF,
            JPG, PNG, WEBP — 20 Mo max.
          </p>
        </div>
      </div>

      {/* Carte upload */}
      <Card className="border-emerald-100">
        <CardContent className="p-5 space-y-4">
          <div className="flex items-center gap-2">
            <Upload className="h-5 w-5 text-emerald-600" />
            <h3 className="font-semibold text-slate-800">
              Téléverser un nouveau document
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                <FolderOpen className="h-4 w-4" />
                Catégorie
              </Label>
              <Select
                value={selectedCategory}
                onValueChange={setSelectedCategory}
              >
                <SelectTrigger
                  className="h-11"
                  data-testid="cooper-doc-category-select"
                >
                  <SelectValue placeholder="Choisir une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.value} value={cat.value}>
                      <span className="flex items-center gap-2">
                        {cat.label}
                        {cat.type === 'custom' && (
                          <Badge
                            variant="outline"
                            className="text-[10px] py-0 px-1.5 text-purple-600 border-purple-300"
                          >
                            Personnalisé
                          </Badge>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label className="text-sm font-medium text-slate-700 flex items-center gap-1">
                <Tag className="h-4 w-4" />
                Libellé personnalisé (optionnel)
              </Label>
              <Input
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder="Ex: RIB compte pro 2026"
                maxLength={120}
                className="h-11"
                data-testid="cooper-doc-custom-label"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-sm font-medium text-slate-700">Fichier</Label>
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic,.heif"
                onChange={handleFileChange}
                className="block w-full text-sm text-slate-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100"
                data-testid="cooper-doc-file-input"
              />
              <Button
                onClick={handleUpload}
                disabled={uploading || !selectedFile}
                className="bg-emerald-600 hover:bg-emerald-700"
                data-testid="cooper-doc-upload-button"
              >
                {uploading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Envoi…
                  </>
                ) : (
                  <>
                    <Upload className="h-4 w-4 mr-2" />
                    Téléverser
                  </>
                )}
              </Button>
            </div>
            {selectedFile && (
              <p className="text-xs text-slate-500">
                {selectedFile.name} — {formatSize(selectedFile.size)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Liste documents */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-slate-800 flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-600" />
              Mes documents ({documents.length})
            </h3>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-400">
              <Loader2 className="h-6 w-6 mx-auto animate-spin" />
              <p className="mt-2 text-sm">Chargement…</p>
            </div>
          ) : documents.length === 0 ? (
            <div className="py-12 text-center" data-testid="cooper-documents-empty">
              <FileText className="h-10 w-10 mx-auto text-slate-300 mb-2" />
              <p className="text-slate-500 text-sm">
                Aucun document téléversé pour le moment.
              </p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="cooper-documents-list">
              {documents.map((doc) => (
                <div
                  key={doc.ged_id}
                  className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-slate-100 rounded-lg border border-slate-200 transition-colors"
                  data-testid={`cooper-doc-item-${doc.ged_id}`}
                >
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm text-slate-800 truncate">
                      {doc.custom_label ||
                        doc.description ||
                        doc.original_filename ||
                        doc.filename}
                    </p>
                    <div className="flex flex-wrap items-center gap-2 mt-1 text-xs text-slate-500">
                      <Badge
                        variant="outline"
                        className="text-[10px] py-0 px-1.5 bg-white"
                      >
                        {doc.doc_type_label || doc.doc_type}
                      </Badge>
                      <span>{formatDate(doc.created_at)}</span>
                      <span>•</span>
                      <span>{formatSize(doc.file_size)}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(doc)}
                    title="Télécharger"
                    data-testid={`cooper-doc-download-${doc.ged_id}`}
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    onClick={() => handleDelete(doc)}
                    title="Supprimer"
                    data-testid={`cooper-doc-delete-${doc.ged_id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
